import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import config from "../config";
import {
  GET_USER_PROFILE,
  POST_FORGET_PASSWORD,
  POST_JWT_LOGIN,
  POST_REGISTER,
} from "./url_helper";
import qs from "qs";
import { clearAuthUser, getAuthToken, readAuthUser } from "./authStorage";
const { api } = config;

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

axios.interceptors.request.use(
  (config) => {
    if (
      config.url === POST_JWT_LOGIN ||
      config.url === POST_FORGET_PASSWORD ||
      config.url === POST_REGISTER
    ) {
      config.headers.Authorization = null;
      return config;
    }

    config.headers.Authorization = `Bearer ${getAuthToken()}`;

    return config;
  },
  (error) => {
    // Handle request errors here
    return Promise.reject(error);
  }
);

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response?.data ? response?.data : response;
  },
  async function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (error?.response?.status === 401 && !(await isSessionValid())) {
      clearAuthUser();
      const loginPath = `${process.env.PUBLIC_URL || ""}/login`;
      if (window.location.pathname !== loginPath) {
        window.location.href = `${window.location.origin}${loginPath}`;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Some endpoints answer 401 for reasons other than the session (a user with no
 * agency, say), so a 401 alone does not mean signed out. Asks the server
 * whether the token itself is still accepted. Concurrent failures share one
 * check.
 */
let sessionCheck: Promise<boolean> | null = null;
const isSessionValid = (): Promise<boolean> => {
  const token = getAuthToken();
  if (!token) return Promise.resolve(false);
  if (!sessionCheck) {
    sessionCheck = fetch(`${api.API_URL}${GET_USER_PROFILE}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.status !== 401)
      .catch(() => true)
      .finally(() => {
        sessionCheck = null;
      });
  }
  return sessionCheck;
};
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Fetches data from the given URL
   */
  get = (url: string, params?: any, headers?: any): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    if (params) {
      //   Object.keys(params).map((key) => {
      //     paramKeys.push(key + "=" + params[key]);
      //     return paramKeys;
      //   });

      //   const queryString =
      //     paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      const queryString = qs.stringify(params);

      response = axios.get(`${url}?${queryString}`, headers);
    } else {
      response = axios.get(`${url}`, headers);
    }

    return response;
  };

  /**
   * Posts the given data to the URL
   */
  create = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.post(url, data);
  };

  createAndUpload = (url: string, data: any): Promise<AxiosResponse> => {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, data, config);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.put(url, data);
  };

  updateAndUpload = (url: string, data: any): Promise<AxiosResponse> => {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    return axios.put(url, data, config);
  };

  put = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedinUser = () => readAuthUser();

export { APIClient, setAuthorization, getLoggedinUser };
